// Money and date formatting shared by the API (PDF rendering) and the web client,
// so a total reads identically on screen and on the invoice.

import type { Currency, Locale } from './types';
import { localeTag } from './i18n/index';

/**
 * Money is carried as a JS number because every amount in this application is a
 * DECIMAL(12,2) — well inside the range where doubles represent cents exactly.
 * Sums are still computed in SQL or from integer cents, never by accumulating
 * formatted strings the way the legacy code did.
 */
export function roundMoney(value: number): number {
  return roundTo(value, 2);
}

/**
 * Rounds half away from zero, which is what MySQL's ROUND() does for DECIMAL
 * arguments and what DECIMAL column assignment does. Matching it matters: the
 * `invoice_lines.line_total` generated column is computed by the database, so if
 * JS rounded half-to-even the API's totals would drift from the stored ones.
 *
 * The epsilon nudge compensates for values like 1.015 being held as
 * 1.0149999999999999, which would otherwise round down.
 */
export function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  const scaled = value * factor;
  const nudged = scaled + Math.sign(scaled) * Math.abs(scaled) * Number.EPSILON;
  return Math.sign(nudged) * Math.round(Math.abs(nudged)) / factor;
}

/** Quantities are DECIMAL(12,3) in the database. */
export function roundQuantity(value: number): number {
  return roundTo(value, 3);
}

/** Multiplies a quantity by a unit price, rounded to cents, matching the
 *  `line_total` generated column in the database. */
export function lineTotal(quantity: number, unitPrice: number): number {
  return roundMoney(quantity * unitPrice);
}

export interface MoneyFormat {
  decimals: number;
  thousandSeparator: string;
  decimalSeparator: string;
  symbol: string;
}

export const DEFAULT_MONEY_FORMAT: MoneyFormat = {
  decimals: 2, thousandSeparator: '.', decimalSeparator: ',', symbol: '€',
};

export function moneyFormatFromCurrency(currency: Currency | undefined): MoneyFormat {
  if (!currency) return DEFAULT_MONEY_FORMAT;
  return {
    decimals: currency.decimals,
    thousandSeparator: currency.thousandSeparator,
    decimalSeparator: currency.decimalSeparator,
    symbol: currency.symbol,
  };
}

/** Formats an amount using the currency's own separators — not the UI locale's,
 *  because the invoice must read the same wherever it is opened. */
export function formatAmount(value: number, format: MoneyFormat = DEFAULT_MONEY_FORMAT): string {
  const negative = value < 0;
  const fixed = Math.abs(roundMoney(value)).toFixed(format.decimals);
  const [whole = '0', fraction = ''] = fixed.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, format.thousandSeparator);
  const body = fraction ? grouped + format.decimalSeparator + fraction : grouped;
  return negative ? `-${body}` : body;
}

export function formatMoney(value: number, format: MoneyFormat = DEFAULT_MONEY_FORMAT): string {
  return `${formatAmount(value, format)} ${format.symbol}`.trim();
}

/** Trims trailing zeros from a quantity: 2.000 -> "2", 1.500 -> "1,5". */
export function formatQuantity(value: number, format: MoneyFormat = DEFAULT_MONEY_FORMAT): string {
  const rounded = Math.round(value * 1000) / 1000;
  if (Number.isInteger(rounded)) return String(rounded);
  return String(rounded).replace('.', format.decimalSeparator);
}

/** `YYYY-MM-DD` — the wire format for dates, and what <input type="date"> wants. */
export function toDateInput(value: string | Date): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function formatDate(value: string | Date, locale: Locale): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(localeTag(locale), {
    day: '2-digit', month: '2-digit', year: 'numeric',
  }).format(d);
}
